import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useRef, useEffect, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Sparkles, Loader2, MapPin, ArrowUpRight, Euro, MessageSquarePlus, Trash2, ArrowLeft, Menu, UtensilsCrossed, Compass, CalendarDays, Wallet, X } from "lucide-react";
import { StarIcon } from "@/components/icons/StarIcon";
import sendIcon from "@/assets/send.svg";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { askAssistant, type ChatMsg } from "@/lib/ai.functions";
import { useCity } from "@/context/CityContext";
import { useResolvedCity } from "@/hooks/use-public-cities";
import { type Item } from "@/data/places";
import { useListedPlaces } from "@/hooks/use-public-places";
import { useReviewStat } from "@/hooks/use-review-stats";
import { z } from "zod";

export const Route = createFileRoute("/ai")({
  validateSearch: z.object({ q: z.string().optional() }),
  head: () => ({
    meta: [
      { title: "whatAI — your finland concierge" },
      { name: "description", content: "Chat with whatAI to find places, restaurants and events tailored to you." },
      { property: "og:title", content: "whatAI — your finland concierge" },
      { property: "og:description", content: "Chat with whatAI to find places, restaurants and events tailored to you." },
      { property: "og:url", content: "https://whatfinland.com/ai" },
    ],
    links: [
      { rel: "canonical", href: "https://whatfinland.com/ai" },
    ],
  }),
  component: AiChatPage,
});

type Turn = ChatMsg & { placeIds?: string[] };
type Thread = { id: string; title: string; updatedAt: number; turns: Turn[] };

const STORAGE_KEY = "what-ai-threads-v1";

function loadThreads(): { threads: Thread[]; activeId: string | null } {
  if (typeof window === "undefined") return { threads: [], activeId: null };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { threads: [], activeId: null };
    const parsed = JSON.parse(raw) as { threads?: Thread[]; activeId?: string | null };
    return { threads: parsed.threads ?? [], activeId: parsed.activeId ?? null };
  } catch {
    return { threads: [], activeId: null };
  }
}

function saveThreads(threads: Thread[], activeId: string | null) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ threads, activeId }));
  } catch { /* quota — ignore */ }
}

function newThreadId() {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

const CATEGORIES: { key: string; label: string; icon: typeof UtensilsCrossed; tint: string; prompts: string[] }[] = [
  {
    key: "eat", label: "eat", icon: UtensilsCrossed, tint: "from-rose-500/15 to-orange-400/10 text-rose-600",
    prompts: ["cozy cafe in Kallio?", "best ramen near me tonight", "romantic dinner under €60pp"],
  },
  {
    key: "do", label: "do", icon: Compass, tint: "from-emerald-500/15 to-teal-400/10 text-emerald-600",
    prompts: ["best sauna spot tonight?", "rainy day in Helsinki — what to do?", "kid-friendly half-day plan"],
  },
  {
    key: "see", label: "see", icon: MapPin, tint: "from-sky-500/15 to-royal/10 text-royal",
    prompts: ["hidden gems near the harbor", "design district walk", "northern lights this week?"],
  },
  {
    key: "plan", label: "plan", icon: Wallet, tint: "from-amber-500/15 to-yellow-400/10 text-amber-600",
    prompts: ["I have €100pp for 4 — sights + dinner", "weekend itinerary, first time visitor", "48 hours in Helsinki on a budget"],
  },
];

const QUICK_CHIPS = ["what's happening today?", "open late tonight", "best brunch nearby", "free things to do"];

function AiChatPage() {
  const { q: initialQuery } = Route.useSearch();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slow, setSlow] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const ask = useServerFn(askAssistant);
  const { city } = useCity();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sentInitial = useRef(false);
  const mobileBlurRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;

    const clearFocus = () => {
      (document.activeElement as HTMLElement | null)?.blur?.();
      mobileBlurRef.current?.focus();
      mobileBlurRef.current?.blur();
    };

    clearFocus();
    const raf = window.requestAnimationFrame(clearFocus);
    const t1 = window.setTimeout(clearFocus, 60);
    const t2 = window.setTimeout(clearFocus, 180);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    const { threads: t, activeId: a } = loadThreads();
    setThreads(t);
    // If we arrive with ?q= we always start a fresh thread.
    setActiveId(initialQuery ? null : (a && t.some((x) => x.id === a) ? a : null));
    setHydrated(true);
  }, [initialQuery]);

  useEffect(() => {
    if (hydrated) saveThreads(threads, activeId);
  }, [threads, activeId, hydrated]);

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeId) ?? null,
    [threads, activeId],
  );
  const turns = activeThread?.turns ?? [];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns.length, loading]);

  function startNewChat() {
    setActiveId(null);
    setShowHistory(false);
    setInput("");
  }

  function openThread(id: string) {
    setActiveId(id);
    setShowHistory(false);
  }

  function deleteThread(id: string) {
    setThreads((prev) => prev.filter((t) => t.id !== id));
    if (activeId === id) setActiveId(null);
  }

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    let threadId = activeId;
    let currentTurns: Turn[] = activeThread?.turns ?? [];
    if (!threadId) {
      threadId = newThreadId();
      currentTurns = [];
      const newT: Thread = { id: threadId, title: trimmed.slice(0, 48), updatedAt: Date.now(), turns: [] };
      setThreads((prev) => [newT, ...prev]);
      setActiveId(threadId);
    }

    const nextTurns: Turn[] = [...currentTurns, { role: "user", content: trimmed }];
    const tid = threadId;
    setThreads((prev) =>
      prev.map((t) => (t.id === tid ? { ...t, turns: nextTurns, updatedAt: Date.now(), title: t.turns.length === 0 ? trimmed.slice(0, 48) : t.title } : t)),
    );
    setInput("");
    setLoading(true);
    setSlow(false);
    const slowTimer = window.setTimeout(() => setSlow(true), 6000);
    try {
      const res = await ask({
        data: {
          messages: nextTurns.map((t) => ({ role: t.role, content: t.content })),
          cityId: city,
        },
      });
      const finalTurns: Turn[] = [...nextTurns, { role: "assistant", content: res.reply, placeIds: res.placeIds }];
      setThreads((prev) =>
        prev.map((t) => (t.id === tid ? { ...t, turns: finalTurns, updatedAt: Date.now() } : t)),
      );
    } catch (e) {
      console.error(e);
      const finalTurns: Turn[] = [...nextTurns, { role: "assistant", content: "Something went wrong. Please try again." }];
      setThreads((prev) =>
        prev.map((t) => (t.id === tid ? { ...t, turns: finalTurns, updatedAt: Date.now() } : t)),
      );
    } finally {
      window.clearTimeout(slowTimer);
      setLoading(false);
      setSlow(false);
    }
  }

  // Auto-send the query passed via ?q= once hydrated
  const sendRef = useRef(send);
  useEffect(() => { sendRef.current = send; });
  useEffect(() => {
    if (!hydrated || sentInitial.current) return;
    const q = initialQuery?.trim();
    if (!q) return;
    sentInitial.current = true;
    sendRef.current(q);
    // Clean the URL so refresh doesn't re-send
    navigate({ to: "/ai", search: {}, replace: true });
  }, [hydrated, initialQuery, navigate]);

  const charCount = input.length;
  const canSend = !loading && input.trim().length > 0;
  const sortedThreads = useMemo(() => [...threads].sort((a, b) => b.updatedAt - a.updatedAt), [threads]);

  const cityName = useResolvedCity(city).name;
  const [mobileNav, setMobileNav] = useState(false);

  return (
    <div className="relative h-[100svh] supports-[height:100dvh]:h-[100dvh] sm:min-h-[calc(100vh-4rem)] sm:h-[calc(100vh-4rem)] overflow-hidden bg-white sm:bg-[hsl(210_40%_98%)]">
      <div ref={mobileBlurRef} tabIndex={-1} aria-hidden className="fixed left-0 top-0 size-px opacity-0 pointer-events-none" />
      {/* Ambient aurora background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-32 size-[520px] rounded-full bg-royal/15 blur-[120px]" />
        <div className="absolute top-1/3 -right-24 size-[460px] rounded-full bg-teal/20 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 size-[420px] rounded-full bg-emerald-300/15 blur-[120px]" />
      </div>

      <div className="mx-auto max-w-7xl px-0 sm:px-5 py-0 sm:py-6 h-full">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-0 sm:gap-4 lg:gap-5 h-full">
          {/* SIDEBAR (desktop) + mobile drawer */}
          <aside className={`${mobileNav ? "fixed inset-0 z-40 p-3 bg-navy/40 backdrop-blur-sm lg:relative lg:bg-transparent lg:p-0" : "hidden lg:block"}`}
            onClick={(e) => { if (e.target === e.currentTarget) setMobileNav(false); }}>
            <div className="h-full max-w-[320px] lg:max-w-none w-full bg-white/80 backdrop-blur-xl border border-white/60 rounded-3xl shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] flex flex-col overflow-hidden">
              {/* Brand */}
              <div className="px-4 pt-4 pb-3 flex items-center gap-3">
                <span className="relative size-10 rounded-2xl bg-gradient-to-br from-royal to-navy grid place-items-center text-white shadow-lg shadow-royal/30">
                  <Sparkles className="size-4" />
                  <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-emerald-400 ring-2 ring-white" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-navy text-[15px] leading-none">what AI</p>
                  <p className="text-[10px] text-navy/50 mt-1">your finland concierge</p>
                </div>
                <button onClick={() => setMobileNav(false)} aria-label="Close menu"
                  className="lg:hidden size-8 grid place-items-center rounded-xl text-navy/60 hover:bg-navy/5">
                  <X className="size-4" />
                </button>
              </div>

              <div className="px-3">
                <button onClick={() => { startNewChat(); setMobileNav(false); }}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-br from-royal to-navy text-white text-sm font-semibold py-2.5 rounded-2xl shadow-md shadow-royal/25 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <MessageSquarePlus className="size-4" /> new chat
                </button>
              </div>

              {/* Threads */}
              <div className="mt-4 px-2 flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.16em] text-navy/50 px-2">recent</p>
                <span className="text-[10px] text-navy/40 px-2">{sortedThreads.length}</span>
              </div>
              <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
                {sortedThreads.length === 0 ? (
                  <p className="text-[11px] text-navy/40 px-2 py-3">no chats yet — start a new one above.</p>
                ) : (
                  sortedThreads.map((t) => {
                    const isActive = t.id === activeId;
                    return (
                      <div key={t.id} className={`group relative rounded-xl border transition-all ${
                        isActive ? "bg-royal/8 border-royal/30" : "bg-white/60 border-transparent hover:bg-white hover:border-navy/10"
                      }`}>
                        <button onClick={() => { openThread(t.id); setMobileNav(false); }}
                          className="w-full text-left px-3 py-2 pr-9">
                          <p className="text-[13px] font-medium text-navy truncate">{t.title || "new chat"}</p>
                          <p className="text-[10px] text-navy/40 mt-0.5">
                            {new Date(t.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {t.turns.filter((x) => x.role === "user").length} msg
                          </p>
                        </button>
                        <button onClick={() => deleteThread(t.id)} aria-label="Delete chat"
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 size-6 grid place-items-center rounded-md text-navy/40 hover:bg-red-50 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100">
                          <Trash2 className="size-3" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="px-3 py-3 border-t border-navy/5">
                <Link to="/" className="flex items-center gap-2 text-[12px] text-navy/60 hover:text-navy px-2 py-1.5 rounded-lg hover:bg-navy/5 transition-colors">
                  <ArrowLeft className="size-3.5" /> back to what
                </Link>
              </div>
            </div>
          </aside>

          {/* MAIN CHAT */}
          <main className="relative bg-white sm:bg-white/70 sm:backdrop-blur-xl border-0 sm:border border-white/60 rounded-none sm:rounded-3xl shadow-none sm:shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] flex flex-col overflow-hidden min-h-0">
            {/* Top bar */}
            <header className="relative shrink-0 border-b border-navy/5 px-3 sm:px-5 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3 bg-white/95 sm:bg-white/60 backdrop-blur">
              <Link to="/" aria-label="Back" title="back"
                className="size-9 grid place-items-center rounded-xl text-navy/70 hover:bg-navy/5 -ml-1.5">
                <ArrowLeft className="size-[18px]" />
              </Link>
              <button onClick={() => setMobileNav(true)} aria-label="Open menu"
                className="lg:hidden size-9 grid place-items-center rounded-xl text-navy/70 hover:bg-navy/5">
                <Menu className="size-[18px]" />
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-navy text-[15px] sm:text-base leading-none truncate">
                  {activeThread?.title ? activeThread.title.slice(0, 50) : "new conversation"}
                </p>
                <p className="text-[10.5px] sm:text-[11px] text-navy/50 mt-1 flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  online · <MapPin className="size-3" /> {cityName.toLowerCase()}
                </p>
              </div>
              <button onClick={startNewChat} aria-label="New chat" title="new chat"
                className="size-9 grid place-items-center rounded-xl text-navy/60 hover:bg-navy/5 hover:text-navy transition-colors -mr-1.5">
                <MessageSquarePlus className="size-[18px]" />
              </button>
            </header>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-6 py-5 min-h-0">
              {turns.length === 0 && !loading ? (
                <EmptyState onPick={send} />
              ) : (
                <div className="max-w-3xl mx-auto space-y-4 animate-[fade-in_300ms_ease-out]">
                  {turns.map((t, i) => (<Message key={i} turn={t} />))}
                  {loading && (
                    <div className="flex gap-2 sm:gap-2.5 items-end animate-[fade-in_200ms_ease-out]">
                      <span className="size-7 sm:size-8 shrink-0 rounded-full bg-gradient-to-br from-royal to-navy grid place-items-center text-white shadow-md shadow-royal/20">
                        <Sparkles className="size-3 sm:size-3.5" />
                      </span>
                      <div className="bg-transparent border-0 shadow-none px-0 py-2 sm:bg-white sm:border sm:border-navy/10 sm:rounded-2xl sm:rounded-bl-sm sm:px-4 sm:py-3 sm:shadow-sm flex flex-col gap-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="size-1.5 rounded-full bg-royal/60 animate-bounce [animation-delay:-0.3s]" />
                          <span className="size-1.5 rounded-full bg-royal/60 animate-bounce [animation-delay:-0.15s]" />
                          <span className="size-1.5 rounded-full bg-royal/60 animate-bounce" />
                        </div>
                        {slow && (
                          <p className="text-[11px] text-navy/60 leading-snug max-w-[240px]">
                            still thinking… taking a little longer than usual.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="shrink-0 border-t border-navy/5 bg-gradient-to-t from-white to-white/60 backdrop-blur px-2.5 sm:px-6 pt-2 sm:pt-3 pb-[calc(env(safe-area-inset-bottom)+22px)] sm:pb-4">
              <div className="max-w-3xl mx-auto">
                {turns.length > 0 && (
                  <>
                    {/* Mobile: 2 full cards, no crop */}
                    <div className="grid grid-cols-2 gap-2 mb-2 sm:hidden">
                      {QUICK_CHIPS.slice(0, 2).map((c) => (
                        <button key={c} type="button" onClick={() => send(c)} disabled={loading}
                          className="text-[12px] font-medium text-navy/80 bg-white border border-navy/10 hover:border-royal hover:text-royal hover:bg-royal/5 disabled:opacity-50 rounded-2xl px-3 py-2.5 transition-colors shadow-[0_4px_14px_-8px_rgba(15,23,42,0.18)] truncate">
                          {c}
                        </button>
                      ))}
                    </div>
                    {/* Desktop: full scroll row */}
                    <div className="hidden sm:flex gap-1.5 mb-2 overflow-x-auto -mx-1 px-1 pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                      {QUICK_CHIPS.map((c) => (
                        <button key={c} type="button" onClick={() => send(c)} disabled={loading}
                          className="shrink-0 text-[11px] text-navy/70 bg-white border border-navy/10 hover:border-royal hover:text-royal hover:bg-royal/5 disabled:opacity-50 rounded-full px-3 py-1.5 transition-colors">
                          {c}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <form onSubmit={(e) => { e.preventDefault(); send(input); }}>
                  <div className={`relative rounded-3xl bg-white border transition-all ${
                    input ? "border-royal/50 shadow-[0_10px_30px_-15px_rgba(37,99,235,0.45)]" : "border-navy/10 shadow-[0_6px_20px_-12px_rgba(15,23,42,0.18)]"
                  }`}>
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
                      }}
                      rows={1}
                      maxLength={500}
                      placeholder="ask anything…"
                      className="w-full resize-none max-h-40 bg-transparent text-navy placeholder:text-navy/40 text-base md:text-[15px] leading-relaxed rounded-3xl pl-4 sm:pl-5 pr-14 sm:pr-16 pt-3 sm:pt-3.5 pb-3 sm:pb-9 outline-none"
                    />
                    <div className="hidden sm:flex absolute left-4 right-16 bottom-2.5 items-center gap-2 text-[10px] text-navy/40 pointer-events-none">
                      <kbd className="px-1.5 py-0.5 rounded bg-navy/5 text-navy/50 font-mono text-[9px]">↵</kbd>
                      <span>send</span>
                      <span>·</span>
                      <kbd className="px-1.5 py-0.5 rounded bg-navy/5 text-navy/50 font-mono text-[9px]">⇧ ↵</kbd>
                      <span>new line</span>
                      <span className="ml-auto tabular-nums">{`${charCount}/500`}</span>
                    </div>
                    <button type="submit" disabled={!canSend} aria-label="Send"
                      className={`absolute right-1.5 sm:right-2 bottom-1.5 sm:bottom-2 size-10 sm:size-11 grid place-items-center rounded-2xl transition-all ${
                        canSend ? "bg-gradient-to-br from-royal to-navy text-white shadow-lg shadow-royal/30 hover:scale-105 active:scale-95" : "bg-navy/10 text-navy/30"
                      }`}>
                      {loading ? <Loader2 className="size-4 animate-spin" /> : <img src={sendIcon} alt="" className="size-4" style={{ filter: "brightness(0) invert(1)" }} />}
                    </button>
                  </div>
                  <p className="hidden sm:block text-[10px] text-navy/40 text-center mt-2.5">
                    powered by the <strong className="text-navy/60">what</strong> catalog · weather-aware · saved on this device
                  </p>
                </form>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onPick }: { onPick: (s: string) => void }) {
  return (
    <div className="max-w-3xl mx-auto py-6 sm:py-10 animate-[fade-in_400ms_ease-out]">
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-1.5 bg-royal/8 text-royal text-[11px] font-medium px-3 py-1 rounded-full">
          <Sparkles className="size-3" /> AI · weather-aware · local
        </span>
        <h1 className="font-heading text-navy text-[28px] sm:text-[36px] leading-tight tracking-tight mt-4">
          hi, I'm <span className="bg-gradient-to-r from-royal to-navy bg-clip-text text-transparent">what AI</span>.
        </h1>
        <p className="text-navy/60 text-sm sm:text-base mt-2 max-w-md mx-auto">
          your finland concierge — ask for places, dinners, plans, or a full day mapped out.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {CATEGORIES.map((cat) => (
          <div key={cat.key}
            className="group bg-white border border-navy/10 rounded-2xl p-4 hover:border-royal/30 hover:shadow-lg hover:shadow-royal/5 transition-all">
            <div className="flex items-center gap-2.5 mb-3">
              <span className={`size-9 rounded-xl bg-gradient-to-br ${cat.tint} grid place-items-center`}>
                <cat.icon className="size-4" />
              </span>
              <p className="font-heading text-navy text-sm">{cat.label}</p>
            </div>
            <div className="space-y-1.5">
              {cat.prompts.map((p) => (
                <button key={p} onClick={() => onPick(p)}
                  className="group/btn w-full text-left text-[12.5px] text-navy/75 hover:text-navy bg-navy/3 hover:bg-royal/8 rounded-xl px-3 py-2 transition-colors flex items-center gap-2">
                  <span className="flex-1 truncate">{p}</span>
                  <ArrowUpRight className="size-3.5 text-navy/30 group-hover/btn:text-royal group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-all shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-navy/40 text-center mt-6 flex items-center justify-center gap-1.5">
        <CalendarDays className="size-3" /> aware of today's date and local weather
      </p>
    </div>
  );
}


function Message({ turn }: { turn: Turn }) {
  if (turn.role === "user") {
    return (
      <div className="flex justify-end animate-[fade-in_200ms_ease-out]">
        <div className="max-w-[85%] bg-royal text-white text-[14.5px] sm:text-[15px] leading-relaxed rounded-2xl rounded-br-md px-3.5 py-2 sm:px-4 sm:py-2.5 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
          {turn.content}
        </div>
      </div>
    );
  }
  const text = turn.content.replace(/\[place:([a-z0-9-]+)\]/gi, "").trim();
  const ITEMS = useListedPlaces();
  const places = (turn.placeIds ?? [])
    .map((id) => ITEMS.find((it) => it.id === id))
    .filter((x): x is Item => Boolean(x));
  return (
    <div className="flex gap-2 sm:gap-2.5 animate-[fade-in_250ms_ease-out]">
      <span className="size-7 sm:size-8 shrink-0 rounded-full bg-gradient-to-br from-royal to-navy grid place-items-center text-white shadow-md shadow-royal/20">
        <Sparkles className="size-3 sm:size-3.5" />
      </span>
      <div className="flex-1 space-y-2 min-w-0">
        {text && (
          <div className="bg-transparent border-0 shadow-none px-0 py-1 sm:bg-white sm:border sm:border-navy/10 sm:rounded-2xl sm:rounded-tl-sm sm:px-4 sm:py-3 sm:shadow-sm">
            <div className="text-navy text-[14.5px] sm:text-[15px] leading-relaxed space-y-2 break-words [overflow-wrap:anywhere]
              [&_p]:m-0 [&_p+p]:mt-2
              [&_strong]:font-semibold [&_strong]:text-navy
              [&_ul]:list-none [&_ul]:m-0 [&_ul]:p-0 [&_ul]:space-y-1.5
              [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:m-0 [&_ol]:space-y-1
              [&_li]:relative [&_li]:pl-5
              [&_ul>li]:before:content-['✦'] [&_ul>li]:before:absolute [&_ul>li]:before:left-0 [&_ul>li]:before:top-0 [&_ul>li]:before:text-royal [&_ul>li]:before:text-xs
              [&_a]:text-royal [&_a]:underline [&_a]:underline-offset-2 [&_a]:font-medium hover:[&_a]:text-navy">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                a: ({ href, children }) => (<a href={href} target="_blank" rel="noopener noreferrer">{children}</a>),
              }}>{text}</ReactMarkdown>
            </div>
          </div>
        )}
        {places.length > 0 && (
          <div className="space-y-2 pt-1">
            <p className="text-[10px] uppercase tracking-[0.14em] text-navy/50 px-1 flex items-center gap-1.5">
              <Sparkles className="size-3 text-royal" /> suggested from what guide
            </p>
            {places.map((p) => <SuggestedPlaceCard key={p.id} place={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function SuggestedPlaceCard({ place }: { place: Item }) {
  const stat = useReviewStat(place.id);
  const hasReviews = stat.count >= 5;

  return (
    <div className="group relative flex gap-3 bg-white border border-navy/10 rounded-2xl p-2 hover:shadow-lg hover:shadow-royal/10 hover:border-royal/40 hover:-translate-y-0.5 transition-all">
      <Link to="/place/$id" params={{ id: place.id }} aria-label={`Open ${place.name}`}
        className="absolute inset-0 rounded-2xl z-0" />
      <div className="relative size-20 rounded-xl overflow-hidden shrink-0 pointer-events-none">
        <img src={place.image} alt={place.name} className="size-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-navy/70 to-transparent" />
        {hasReviews && (
          <span className="absolute bottom-1 left-1 flex items-center gap-0.5 bg-white/90 backdrop-blur text-navy text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
            <StarIcon className="size-2.5 text-[var(--star)]" /> {stat.avg.toFixed(1)} <span className="text-navy/55">({stat.count})</span>
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 py-0.5 flex flex-col relative pointer-events-none">
        <div className="flex items-start justify-between gap-1">
          <p className="font-heading text-navy text-sm truncate flex-1">{place.name}</p>
          <ArrowUpRight className="size-3.5 text-navy/30 group-hover:text-royal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
        </div>
        <p className="text-[11px] text-navy/60 truncate flex items-center gap-1 mt-0.5">
          <MapPin className="size-3 shrink-0" /> {place.area} · {place.category}
        </p>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap pointer-events-auto">
          {place.price && (
            <span className="inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-medium px-1.5 py-0.5 rounded-md">
              <Euro className="size-2.5" />{place.price.replace(/€|From|from/g, "").trim() || place.price}
            </span>
          )}
          {place.link && (
            <a href={place.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
              className="relative z-10 inline-flex items-center gap-0.5 bg-royal/10 text-royal text-[10px] font-medium px-1.5 py-0.5 rounded-md hover:bg-royal hover:text-white transition-colors">
              Website <ArrowUpRight className="size-2.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

